import { z } from 'zod';
import { Translations } from 'types/Common';
import { UploadedFile } from 'store/files';
import {
  surveyPendingSubmissionsFiltersSchema,
  surveysFiltersSchema,
  surveySubmissionsFiltersSchema,
} from './surveys.schemas';

export const surveyTypes = ['survey', 'form', 'training'] as const;
export type SurveyType = (typeof surveyTypes)[number];

export const surveyStatuses = [
  'draft',
  'pending',
  'in_progress',
  'ended',
] as const;
export type SurveyStatus = (typeof surveyStatuses)[number];

export const surveyChoiceAnswerTypes = [
  'single_choice_response',
  'multiple_choice_response',
] as const;
export type SurveyChoiceAnswerType = (typeof surveyChoiceAnswerTypes)[number];

export const surveyRatingScaleAnswerTypes = [
  'rating_scale_numbers_5',
  'rating_scale_numbers_7',
  'rating_scale_numbers_10',
  'rating_scale_emoji_3',
  'rating_scale_emoji_5',
] as const;
export type SurveyRatingScaleAnswerType =
  (typeof surveyRatingScaleAnswerTypes)[number];

export const surveyAnswerTypes = [
  ...surveyChoiceAnswerTypes,
  'text_response',
  'information_page',
  ...surveyRatingScaleAnswerTypes,
] as const;
export type SurveyAnswerType = (typeof surveyAnswerTypes)[number];

export const surveyChoiceAnswerFormats = [
  'text',
  'image',
  'text_image',
] as const;
export type SurveyChoiceAnswerFormat =
  (typeof surveyChoiceAnswerFormats)[number];

export const notificationMethods = ['email', 'appNotification'] as const;
export type NotificationMethod = (typeof notificationMethods)[number];

export const surveyAssignmentStatuses = ['on', 'off'] as const;
export type SurveyAssignmentStatus = (typeof surveyAssignmentStatuses)[number];

export const trainingDifficulties = ['easy', 'medium', 'hard'] as const;
export type TrainingDifficulty = (typeof trainingDifficulties)[number];

export const trainingTones = ['friendly', 'formal'] as const;
export type TrainingTone = (typeof trainingTones)[number];

export interface BaseSurvey {
  id: string;
  name: string;
  type: SurveyType;
  status: SurveyStatus;
  notificationMethods: NotificationMethod[];
  invitationSentTo: number;
  currentRecipients: number;
  completedBy: number;
  submittedBy: number;
  passedBy: number;
  scoringEnabled: boolean;
  minScore: number;
  retries: number;
  rewardPoints: number;
  anonymous: boolean;
  hasWebAccess: boolean;
  creationDate: string;
  publishDateTime: string | null;
  endDateTime: string | null;
}

export interface Survey extends BaseSurvey {
  questions: Array<{
    id: string;
    type: SurveyAnswerType;
    index: number;
    isRequired: boolean;
    creationDate: string;
    translations: Translations<{
      text: string;
      description: string;
      attachments: UploadedFile[];
      videos: string[];
      imageId: string | null;
      imageUrl: string | null;
    }>;
    options: Array<{
      index: number;
      translations: Translations<{
        text: string;
        imageId: string | null;
        imageUrl: string | null;
        isCorrect: boolean;
      }>;
    }>;
    answers: Array<{
      id: string;
      optionAnswer: string[];
      textAnswer: string;
    }>;
    metadata: {
      withImage: boolean;
      withDescription: boolean;
      withAttachments: boolean;
      withVideo: boolean;
      choiceAnswerFormat: SurveyChoiceAnswerFormat;
      conditionalJumpRules: Array<{
        answerIndex: number;
        questionIndex: number;
      }>;
    };
  }>;
  translations: Translations<{
    title: string;
    description: string;
    imageId: string | null;
    imageUrl?: string;
    attachments: UploadedFile[];
    videos: string[];
  }>;
  metadata: {
    withDescription: boolean;
    withImage: boolean;
    withAttachments: boolean;
    withVideo: boolean;
  };
}

export interface SurveyGroupAssignment {
  id: string;
  groupId: string;
  groupName: string;
  employeeCount: number;
  assignmentStatus: SurveyAssignmentStatus;
}

export interface SurveyResults extends BaseSurvey {
  questions: Array<
    Pick<
      Survey['questions'][number],
      'id' | 'type' | 'index' | 'creationDate' | 'translations' | 'options'
    > & {
      answeredByCount: number;
      optionResponseStats: Array<{
        optionAnswer: number[];
        answeredByCount: number;
        answeredByDistinctEmployeeCount: number;
      }>;
    }
  >;
  anonymous: boolean;
}

export interface BaseSurveyPendingSubmission {
  id: string;
  employee: { name: string } | null;
  company: { id: string; name: string } | null;
  department: { id: string; name: string } | null;
}

export interface BaseSurveySubmission {
  id: string;
  employee: { id: string; name: string } | null;
  company: { id: string; name: string } | null;
  department: { name: string } | null;
  startDate: string;
  completionDate: string | null;
  score: number;
  submissionAttempt: number;
}

export interface SurveySubmission extends BaseSurveySubmission {
  submissionAttempt: number;
  totalSubmissions: number;
  answers: Array<{
    questionId: string;
    optionAnswer: number[];
    textAnswer: string;
  }>;
}

export type SurveysFilters = z.infer<typeof surveysFiltersSchema>;

export type SurveyPendingSubmissionsFilters = z.infer<
  typeof surveyPendingSubmissionsFiltersSchema
>;

export type SurveySubmissionsFilters = z.infer<
  typeof surveySubmissionsFiltersSchema
>;
